import { Stack, Typography, TextField, InputLabel, Grid } from '@mui/material';

import { useState, ChangeEvent, useEffect, useRef } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { ReactComponent as CloseIcon } from 'common/assets/images/close.svg';
import { ReactComponent as ToastIcon } from 'common/assets/images/toast-reset.svg';
import ButtonComp from 'common/components/Button/ButtonComp';
import { Loader } from 'common/components/Loader/Loader';
import AlertComponent from 'common/components/ToastMessage/ToastMessage';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import { TOAST_MESSAGE_STATUS } from 'common/constants/ToastMessageConstants';
import { RootState } from 'common/store/AppStore';
import { setToastMessage } from 'common/store/reducer/commonReducer';
import { toSentenceCase } from 'common/utils/utilFunctions';

import { resetUserActivities } from './api';
import { RESET_LAYOUT, RESET_TOAST_MESSAGE } from './ResetConstants';

import styles from './ResetLayout.module.scss';

const ResetLayout = () => {
	/* Destructured variables */
	const {
		LAYOUT_HEADER,
		RESET_LABEL,
		LIST_ITEM_1,
		LIST_ITEM_2,
		RESET_DESCRIPTION,
		RESET_BUTTON,
		RESET_TEXT,
		RESET_PLACEHOLDER,
	} = RESET_LAYOUT;
	const { TOAST_TITLE, SUCCESS_MESSAGE, FAILURE_MESSAGE } = RESET_TOAST_MESSAGE;
	const { OK } = HTTP_STATUS_CODES;
	const { TOAST_SUCCESS, TOAST_ERROR } = TOAST_MESSAGE_STATUS;
	const { borderColor, borderSpacing, borderRadius } = styles;

	/* Local states */
	const dispatch = useDispatch();
	const [resetText, setResetText] = useState<string>('');
	const [toastTitle, setToastTitle] = useState<string>('');
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const inputRef = useRef<HTMLInputElement | null>(null);

	/* Local variables */
	const { isToastOpen, severity, message } = useSelector(
		(state: RootState) => state.common.toastMessage,
	);

	const style = {
		'& .MuiOutlinedInput-root': {
			'& fieldset': {
				border: `${borderSpacing} solid ${borderColor}`,
				borderRadius,
			},

			'&.Mui-focused fieldset': {
				border: `${borderSpacing} solid ${borderColor}`,
				borderRadius,
			},
		},
		'& .MuiInputBase-root.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline':
			{
				border: `${borderSpacing} solid ${borderColor}`,
				borderRadius,
			},
	};

	/**
	 * @description method change the text of reset field
	 * @param event
	 */
	const onResetChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
		setResetText(event?.target?.value);
	};

	/**
	 * @description method to close the toast
	 * @param event
	 */
	const onToastCloseHandler = () => {
		dispatch(
			setToastMessage({
				message: '',
				severity: '',
				isToastOpen: false,
			}),
		);
	};

	/**
	 * @description method to click on reset button
	 */
	const onResetClickHandler = async () => {
		setIsLoading(true);
		const response = await resetUserActivities();
		setIsLoading(false);
		if (response?.statusCode === OK) {
			setToastTitle(toSentenceCase(TOAST_TITLE));
			dispatch(
				setToastMessage({
					message: toSentenceCase(SUCCESS_MESSAGE),
					severity: TOAST_SUCCESS,
					isToastOpen: true,
				}),
			);
			setResetText('');
			setTimeout(() => {
				onToastCloseHandler();
			}, 5000);
		} else {
			dispatch(
				setToastMessage({
					message: toSentenceCase(FAILURE_MESSAGE),
					severity: TOAST_ERROR,
					isToastOpen: true,
				}),
			);
		}
	};

	/* method to close the toast on umount of component */
	useEffect(
		() => () => {
			onToastCloseHandler();
		},
		[],
	);

	return (
		<Stack>
			{isToastOpen &&
				severity?.toLowerCase() === TOAST_SUCCESS.toLowerCase() && (
					<div className={styles['msg-popup']}>
						<Stack className={styles['popup-container']}>
							<Typography className={styles['popup-title']}>
								<ToastIcon />
								<span className={styles['title-txt']} data-testid="success-msg">
									{toastTitle}
								</span>
							</Typography>
							<CloseIcon
								fontSize="medium"
								onClick={onToastCloseHandler}
								className={styles['close-msg']}
								data-testid="close-icon"
							/>
						</Stack>
						<Typography className={styles['msg-text']}>{message}</Typography>
					</div>
				)}
			<Typography className={styles['layout-header-text']}>
				{toSentenceCase(LAYOUT_HEADER)}
			</Typography>
			<Typography className={styles['layout-body-container']}>
				{isLoading ? (
					<Loader show className={styles['loader-styling']} />
				) : (
					<>
						<p className={styles['body-label']}>
							{toSentenceCase(RESET_LABEL)}
						</p>
						<li className={styles['reset-list-items']}>
							{toSentenceCase(LIST_ITEM_1)}
						</li>
						<li className={styles['reset-list-items']}>{LIST_ITEM_2}</li>
						<p>{RESET_DESCRIPTION}</p>
						<Grid container spacing={1}>
							<Grid
								item
								xs={9.5}
								sm={9.5}
								md={9.5}
								xl={9.5}
								lg={9.5}
								className={styles['text-field-container']}
							>
								<TextField
									fullWidth
									value={resetText}
									onChange={onResetChangeHandler}
									InputProps={{
										inputProps: { 'data-testid': 'reset-field' },
									}}
									sx={style}
									className={styles['reset-field']}
									inputRef={inputRef}
									disabled={isLoading}
								/>
								<InputLabel
									htmlFor="reset"
									className={`${styles['reset-label']}`}
									onClick={() => {
										inputRef.current?.focus();
									}}
									data-testid="reset-label"
								>
									{!resetText ? (
										<>
											<span>{RESET_PLACEHOLDER}</span>
											<span className={`${styles.required}`}> *</span>
										</>
									) : (
										''
									)}
								</InputLabel>
							</Grid>
							<Grid
								item
								xs={2.5}
								sm={2.5}
								md={2.5}
								xl={2.5}
								lg={2.5}
								className={styles['btn-container']}
							>
								<ButtonComp
									btnType="primary"
									clickHandler={onResetClickHandler}
									disabled={
										resetText?.toLowerCase() !== RESET_TEXT.toLowerCase()
									}
									data-testid="reset-button"
									compClass={styles['btn-reset']}
								>
									{RESET_BUTTON}
								</ButtonComp>
							</Grid>
						</Grid>
					</>
				)}
			</Typography>
			<AlertComponent
				open={
					severity?.toLowerCase() === TOAST_ERROR.toLowerCase()
						? isToastOpen || false
						: false
				}
				toastMessage={message ?? ''}
				severity={TOAST_ERROR}
				handleCloseMessage={onToastCloseHandler}
				vertical="top"
				horizontal="right"
			/>
		</Stack>
	);
};

export default ResetLayout;
